import { z } from "zod";

/**
 * #346: customer-perceived silence per turn, derived by the
 * ConversationAnalyser (#214) from Contact Lens segment offsets. Numbers
 * only - no field here can carry a word of the conversation (ADR-0015 §5,
 * ADR-0006). It is the `perceived_wait` field of the `ContactAnalysis`
 * row. Method and limits: design.md "Perceived wait".
 */

const Millis = z.number().int().nonnegative();
const Count = z.number().int().nonnegative();

/**
 * Upper edges (ms, exclusive) of the wait histogram. Fixed, so a day's
 * distribution is the sum of its contacts' buckets (JourneyDailyCounts
 * cannot add percentiles). Waits over the last edge are past the 10 s
 * proximity guard and never measured.
 */
export const PERCEIVED_WAIT_BUCKET_EDGES_MS = [500, 1000, 1500, 2000, 3000, 5000, 10000] as const;
export const PerceivedWaitBucketSchema = z.enum(["lt500", "lt1000", "lt1500", "lt2000", "lt3000", "lt5000", "lt10000"]);
export type PerceivedWaitBucket = z.infer<typeof PerceivedWaitBucketSchema>;

export const WaitDistributionSchema = z.strictObject({
  /** Customer turns answered within the proximity guard, so measured. */
  turns: Count,
  /** Null when no turn was measured. */
  p50_ms: Millis.nullable(),
  p90_ms: Millis.nullable(),
  max_ms: Millis.nullable(),
  buckets: z.record(PerceivedWaitBucketSchema, Count),
});
export type WaitDistribution = z.infer<typeof WaitDistributionSchema>;

/** Where AI-agent spans exist: orchestration, first token, and the STT+TTS remainder. */
export const WaitDecompositionSchema = z.strictObject({
  matched_by_text: Count,
  matched_by_position: Count,
  unmatched: Count,
  orchestration_p50_ms: Millis.nullable(),
  first_token_p50_ms: Millis.nullable(),
  /** Speech-to-text and text-to-speech together; never split (design.md). */
  speech_processing_p50_ms: Millis.nullable(),
});
export type WaitDecomposition = z.infer<typeof WaitDecompositionSchema>;

export const PerceivedWaitSchema = z.strictObject({
  /** Bumped whenever a threshold or rule below changes, so rows stay comparable. */
  method_version: z.literal(1),
  /** Whole contact, both reply sides. */
  overall: WaitDistributionSchema,
  /** The reply came from a person. */
  agent: WaitDistributionSchema,
  /** The reply came from the IVR, a bot or an AI agent. */
  system: WaitDistributionSchema,
  /** Customer: talked over a prompt or the agent. Agent: the reply started before the customer finished (left out of the distribution). */
  barge_ins: z.strictObject({ customer: Count, agent: Count }),
  /** More than 3 s between the previous reply ending and the customer speaking: think time, flagged. */
  pauses_over_3s: Count,
  /** Customer turns whose next reply was more than 10 s away (a transfer or a queue, not processing). */
  excluded_by_proximity: Count,
  /** Customer turns with no reply after them (the call ended). */
  unanswered: Count,
  /** Measured waits over five times the contact's median - flagged, kept in the distribution. */
  anomalies: Count,
  /** Segments without usable offsets, or from a participant we cannot place. */
  segments_skipped: Count,
  decomposition: WaitDecompositionSchema.optional(),
});
export type PerceivedWait = z.infer<typeof PerceivedWaitSchema>;
